import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import { MecordColors } from "Constants/Color";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { ClipboardList, FileText } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchPOAnalytics } from "slices/Procurement/purchase-order/purchase-order/thunk";
import { fetchApprovedPurchaseRequest } from "slices/Procurement/purchase-order/approved-pr/thunk";
import { fetchPurchaseOrder } from "slices/Procurement/purchase-order/purchase-order/thunk";
import ApprovedPR from "./ApprovedPR/ApprovedPR";
import PurchaseOrderList from "./PurchaseOrder/PurchaseOrder";
import { clearAllTableStates } from "utils/clearTableStates";

interface PurchaseOrderSectionProps {
  noWrapper?: boolean;
}

const PurchaseOrder: React.FC<PurchaseOrderSectionProps> = ({ noWrapper = false }) => {
  const dispatch = useDispatch<any>();
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("purchaseOrderTab")) || 1
  );
  const navigate = useNavigate();
  const location = useLocation();
  const isTagsSearchMain =
    noWrapper && location.pathname.startsWith("/tags-search");
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  const [tagsTabSummary, setTagsTabSummary] = useState({
    loading: false,
    approvedPrCount: 0,
    purchaseOrderCount: 0,
    hasError: false,
  });

  const { canCreate } = usePermission(
    app_modules?.purchase_management?.app_name,
    app_modules?.purchase_management?.modules?.purchase_order
  );

  useEffect(() => {
    const incomingTab = location.state?.tab;
    if (incomingTab) {
      setTab(incomingTab);
      clearAllTableStates()
      sessionStorage.setItem("purchaseOrderTab", incomingTab.toString());
    }
  }, [location.state]);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      const newTab = newValue + 1; // MUI tabs are 0-indexed, our tabs are 1-indexed
      clearAllTableStates()
      setTab(newTab);
      sessionStorage.setItem("purchaseOrderTab", newTab.toString());
    },
    []
  );

  // Analytics cards data - memoized to prevent re-creation on every render
  const selectPOs = createSelector(
    (state: any) => state.POreducer,
    (po) => ({
      POList: po.POList,
      rowCount: po.rowCount,
      loading: po.loading,
      error: po.error,
      analyticsData: po.analyticsData,
      loadingAnalytics: po.loadingAnalytics,
    })
  );

  const { analyticsData } = useSelector(selectPOs);
  useEffect(() => {
    dispatch(fetchPOAnalytics());
  }, [dispatch]);

  const statuses = useMemo(
    () => [
      {
        label: "Total Purchase Order",
        icon: <FileText size={18} />,
        bgColor: "bg-blue-50",
        textColor: "text-blue-600",
        count: analyticsData?.total_count ?? 0,
        // subLabel: "Total approved purchase requests",
      },
      {
        label: "Pending PO Inward",
        icon: <ClipboardList size={18} />,
        bgColor: "bg-red-50",
        textColor: "text-red-600",
        count: analyticsData?.pending_count ?? 0,
        // subLabel: "Awaiting purchase order",
      },
      {
        label: "Partially Inwarded",
        icon: <FileText size={18} />,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-600",
        count: analyticsData?.partially_inwarded_count ?? 0,
        // subLabel: "All purchase orders",
      },
      {
        label: "Complete Inwarded",
        icon: <FileText size={18} />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        count: analyticsData?.complete_inwarded_count ?? 0,
        // subLabel: "Awaiting delivery",
      },
    ],
    [analyticsData]
  );

  // Memoized callback for child component tab changes
  const handleChildTabChange = useCallback((val: number) => {
    setTab(val);
    sessionStorage.setItem("purchaseOrderTab", val.toString());
  }, []);

  const sectionHeader = (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <h6 className="card-title mb-0">Purchase Order</h6>

      </div>

      <div className="d-flex align-items-center justify-content-between mt-1 pb-1 mb-1">
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={tab - 1}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                fontFamily: "'Poppins', sans-serif",
                textTransform: "none",
                color: "#555555",
                transition: "color 0.2s ease",
              },
              "& .MuiTab-root:hover": {
                color: "#d2363a",
              },
              "& .Mui-selected": {
                color: "#d2363a !important",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#d2363a",
                height: "3px",
                transition: "all 0.3s ease",
              },
            }}
          >
            <Tab label="Approved PR" />
            <Tab label="Purchase Order" />
          </Tabs>
          <Divider
            sx={{
              mt: 0,
              borderColor: "#c35f61",
            }}
          />
        </Box>
      </div>
    </>
  );

  useEffect(() => {
    if (!isTagsSearchMain || !tagFromUrl) return;
    let mounted = true;
    setTagsTabSummary((prev) => ({ ...prev, loading: true, hasError: false }));

    Promise.all([
      dispatch(
        fetchApprovedPurchaseRequest({
          page: 1,
          pageSize: 1,
          sort: "",
          order: "",
          search: tagFromUrl,
          type: "tag",
          search_type: "tag",
          filters: [],
        })
      ).unwrap(),
      dispatch(
        fetchPurchaseOrder({
          page: 1,
          pageSize: 1,
          sort: "",
          order: "",
          search: tagFromUrl,
          type: "tag",
          search_type: "tag",
          filters: [],
        })
      ).unwrap(),
    ])
      .then(([approvedRes, poRes]) => {
        if (!mounted) return;
        setTagsTabSummary({
          loading: false,
          approvedPrCount: approvedRes?.rowCount ?? 0,
          purchaseOrderCount: poRes?.rowCount ?? 0,
          hasError: false,
        });
      })
      .catch(() => {
        if (!mounted) return;
        setTagsTabSummary({
          loading: false,
          approvedPrCount: 0,
          purchaseOrderCount: 0,
          hasError: true,
        });
      });

    return () => {
      mounted = false;
    };
  }, [dispatch, isTagsSearchMain, tagFromUrl]);

  const hideTagsSearchPurchaseOrderSection =
    isTagsSearchMain &&
    !tagsTabSummary.loading &&
    !tagsTabSummary.hasError &&
    tagsTabSummary.approvedPrCount === 0 &&
    tagsTabSummary.purchaseOrderCount === 0;

  if (hideTagsSearchPurchaseOrderSection) {
    return null;
  }

  const forceShowOnTagsSearch =
    isTagsSearchMain && !hideTagsSearchPurchaseOrderSection;

  const sectionBody =
    tab === 1 ? (
      <ApprovedPR
        handleTabChange={handleChildTabChange}
        noWrapper={true}
        forceShowOnTagsSearch={forceShowOnTagsSearch}
      />
    ) : tab === 2 ? (
      <PurchaseOrderList
        handleTabChange={handleChildTabChange}
        noWrapper={true}
        forceShowOnTagsSearch={forceShowOnTagsSearch}
      />
    ) : null;

  if (noWrapper) {
    return (
      <Fragment>
        {sectionHeader}
        <div className="mt-3">{sectionBody}</div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Container fluid className="page-content rounded-6">
        {/* <Row className="mb-3">
          <AnalyticsCards statuses={statuses} />
        </Row> */}

        <Row id="listPage">
          <Col lg={12}>
            <Card>
              <Card.Header>{sectionHeader}</Card.Header>
              <Card.Body>{sectionBody}</Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PurchaseOrder;
